import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation/Navigation';
import BetaBanner from '../components/Navigation/BetaBanner';
import ProgressSteps from '../components/Navigation/ProgressSteps';
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay';
import { useBook } from '../contexts/BookContext';
import { DESIGN_STATUS_MESSAGES, ACTIVE_DESIGN_STATUSES } from '../constants/designStatuses';

// How long the processing doc can go without an update before we consider it
// stale/stuck and offer the user a way to cancel (matches EditPage's CANCEL_TIMEOUT_MS)
const STALE_CANCEL_TIMEOUT_MS = 6 * 60 * 1000; // 6 minutes

function GlobalDesignProcessingOverlay() {
  const { currentBook, processingStatus, processingUpdatedAt, loadingUIMessage, cancelDesignProcessing } = useBook();
  const location = useLocation();
  const navigate = useNavigate();
  const [staleTimerReached, setStaleTimerReached] = useState(false);

  // Only show on /world and /design routes; /edit renders its own overlay
  const isWorldOrDesign = location.pathname.startsWith('/world') || location.pathname.startsWith('/design');
  const isActive = processingStatus && ACTIVE_DESIGN_STATUSES.has(processingStatus);

  // Offer cancel once the processing doc has gone STALE_CANCEL_TIMEOUT_MS without an update
  useEffect(() => {
    setStaleTimerReached(false);
    if (!isActive || !processingUpdatedAt) return undefined;
    const elapsed = Date.now() - processingUpdatedAt;
    if (elapsed >= STALE_CANCEL_TIMEOUT_MS) {
      setStaleTimerReached(true);
      return undefined;
    }
    const timer = setTimeout(() => setStaleTimerReached(true), STALE_CANCEL_TIMEOUT_MS - elapsed);
    return () => clearTimeout(timer);
  }, [isActive, processingStatus, processingUpdatedAt]);

  if (!isWorldOrDesign || !isActive || !currentBook?.id) return null;

  // Derive progress/text directly from processingStatus (same mapping as the canvas
  // placeholder) so the minimized overlay never shows a stale loadingUIMessage
  const mapped = DESIGN_STATUS_MESSAGES[processingStatus];
  const states = {
    progress: mapped?.progress ?? loadingUIMessage?.progress ?? 0,
    text: mapped?.message || loadingUIMessage?.text || loadingUIMessage?.message || 'Generating your design...'
  };

  return (
    <LoadingOverlay
      key={`${currentBook.id}-design-processing`}
      states={states}
      onComplete={() => {}}
      loadingFor="design"
      canGoToDesign={true}
      goToDesignRoute={() => navigate(`/design/${currentBook.id}`)}
      goToWorldRoute={() => navigate(`/world/${currentBook.id}`)}
      defaultMinimized={true}
      canCancel={staleTimerReached}
      onCancel={cancelDesignProcessing}
    />
  );
}

export default function WizardLayout() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="sticky top-0 z-50">
        <Navigation />
        <BetaBanner />
      </div>
      <div className="flex-1 py-8 mb-16">
        <ProgressSteps />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <Outlet />
        </main>
      </div>
      <GlobalDesignProcessingOverlay />
    </div>
  );
}
